import styled from 'styled-components';
import * as S from './analysis.style';

import { useEffect, useState } from 'react';
import { RealSalesDataItem, RealSalesFilter } from '../../data';
import DetailService from '../../../../service/detail';
import UnitSwitch from '../../../../func/components/UnitSwitch';
import { useReportContext } from '../../../../context/DetailContext';
import { formatToEokMan } from '../../../../func/Function';
import Pagination from '../../../components/pagination/Pagination';

interface Props {
  sameItemAddr: string;
}

// 동일 물건 매물 가격
function SameRealSale({ sameItemAddr }: Props) {
  const { iidx } = useReportContext();
  const [realSalesFilter, setRealSalesFilter] = useState<RealSalesFilter>({
    iidx: Number(iidx) ?? 0,
    b_idx: 0,
    distance: 500,
    limit: 6,
    offset: 0,
    is_same1: false,
    is_same2: false,
  });

  const [totalCount, setTotalCount] = useState(0);
  const [realSalesList, setRealSalesList] = useState<RealSalesDataItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [unit, setUnit] = useState('평');
  const [loading, setLoading] = useState(false);

  const getRealSalesList = async (data: RealSalesFilter) => {
    setLoading(true);
    try {
      const { iidx, b_idx, distance, limit, offset, is_same1, is_same2 } = data;
      const res = await DetailService.realSales(
        iidx,
        b_idx,
        distance,
        limit,
        offset,
        is_same1,
        is_same2,
      );
      setRealSalesList(res.data.sale);
      setTotalCount(res.data.sale_total);
    } catch (e) {
      console.log('GetAroundRealDealPriceList', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (iidx !== '') {
      getRealSalesList(realSalesFilter);
    }
  }, [iidx, realSalesFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    setRealSalesFilter({ ...realSalesFilter, offset: (page - 1) * 6 });
  };

  if (
    realSalesFilter.is_same1 === false &&
    realSalesFilter.is_same2 === false &&
    !loading &&
    realSalesList.length === 0
  )
    return null;

  return (
    <Container id="list-top-real">
      <S.ContentTitleContainer>
        <S.TitleWrapper>
          <S.TitleBox>
            <S.TitleContent>
              <img src="/assets/images/icon/icon_sameAddress.svg" alt="" />
              <div>동일 물건 매물 가격</div>
            </S.TitleContent>
            <UnitSwitch unit={unit} setUnit={setUnit} />
          </S.TitleBox>
          <div>
            {sameItemAddr ? <S.BlueTag>{sameItemAddr}</S.BlueTag> : null}
          </div>
        </S.TitleWrapper>
        <S.CheckboxWrapper>
          <S.Checkbox
            $isOn={realSalesFilter.is_same1}
            onClick={() => {
              setRealSalesFilter({
                ...realSalesFilter,
                is_same1: !realSalesFilter.is_same1,
                offset: 0,
              });
              setCurrentPage(1);
            }}
          >
            <img
              src={`/assets/images/check/${realSalesFilter.is_same1 ? 'circle-checked-blue' : 'circle-checked-gray-outline'}.svg`}
              alt=""
            />
            동일면적 찾기
          </S.Checkbox>
          <S.Checkbox
            $isOn={realSalesFilter.is_same2}
            onClick={() => {
              setRealSalesFilter({
                ...realSalesFilter,
                is_same2: !realSalesFilter.is_same2,
                offset: 0,
              });
              setCurrentPage(1);
            }}
          >
            <img
              src={`/assets/images/check/${realSalesFilter.is_same2 ? 'circle-checked-blue' : 'circle-checked-gray-outline'}.svg`}
              alt=""
            />
            동일층수 찾기
          </S.Checkbox>
        </S.CheckboxWrapper>
      </S.ContentTitleContainer>

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid #E5E8EA',
        }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
          }}
        >
          <thead style={{ width: '100%' }}>
            <tr>
              <S.TableHeadTh width="16%" rowSpan={1}>
                거래일
              </S.TableHeadTh>
              <S.TableHeadTh width="28%" colSpan={1}>
                금액(단가 평/m²)
              </S.TableHeadTh>
              <S.TableHeadTh width="28%" rowSpan={1}>
                전용 면적({unit === '평' ? '평' : 'm²'}) <br />
                (층)
              </S.TableHeadTh>
              <S.TableHeadTh width="28%" rowSpan={1}>
                거리
              </S.TableHeadTh>
            </tr>
          </thead>
          <tbody style={{ width: '100%' }}>
            {realSalesList.length > 0 &&
              realSalesList.map((data, index) => (
                <tr style={{ borderBottom: '1px solid #e8e9ea' }} key={index}>
                  <S.TablebodyTd width="16%">
                    {data.reg_date.split('T')[0]}
                  </S.TablebodyTd>
                  <S.TablebodyTd width="28%" style={{ textAlign: 'end' }}>
                    {(data.han_prc ?? '').trim().endsWith('억')
                      ? `${data.han_prc}원`
                      : `${data.han_prc}만원`}
                    <br></br>(
                    {unit === '평'
                      ? formatToEokMan(data.pyeong_prc)
                      : formatToEokMan(data.meter_prc)}
                    / {unit === '평' ? '평' : 'm²'})
                  </S.TablebodyTd>
                  <S.TablebodyTd width="28%">
                    {unit === '평' ? data.pyeong : data.area}
                    {unit === '평' ? '평' : 'm²'}
                    <br />({data.flr_info}층)
                  </S.TablebodyTd>
                  <S.TablebodyTd width="28%">{data.distance}m</S.TablebodyTd>
                </tr>
              ))}

            {realSalesList.length === 0 && NoDataMessage()}
          </tbody>
        </table>
      </div>
      {realSalesList.length > 0 && (
        <Pagination
          removeMargin={true}
          currentPage={currentPage}
          totalPages={Math.ceil(Number(totalCount) / 6)}
          onPageChange={handlePageChange}
          scrollTargetId="list-top-real"
        />
      )}
    </Container>
  );
}

export default SameRealSale;

function NoDataMessage() {
  return (
    <tr style={{ borderBottom: '1px solid #e8e9ea' }}>
      <S.TablebodyTd width="100%" colSpan={4}>
        최근 10년 실거래 이력이 없습니다.
      </S.TablebodyTd>
    </tr>
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  background: white;
`;
